import React, { useState, useMemo, Suspense } from "react";
import { FaPlus, FaMinus, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import Swal from "sweetalert2";
import "./Contact.css";
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet-async";

// Lazy-loaded map
const Map = React.lazy(() => import("./Map"));

// FAQ Item
const FAQItem = React.memo(({ faq, isOpen, onClick }) => (
  <div className={`faq-card ${isOpen ? "active" : ""}`} onClick={onClick}>
    <div className="faq-question">
      <span>{faq.question}</span>
      <span className="faq-icon">{isOpen ? <FaMinus /> : <FaPlus />}</span>
    </div>
    {isOpen && <div className="faq-answer">{faq.answer}</div>}
  </div>
));

export default function Contact() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqData = useMemo(
    () => [
      {
        question: "What types of digital marketing services do you offer?",
        answer:
          "We offer SEO, PPC, social media marketing, email marketing, and content marketing services tailored to your business goals.",
      },
      {
        question: "What kind of businesses do you serve?",
        answer:
          "We work with startups, small businesses, and enterprises across industries including retail, healthcare, finance, and technology.",
      },
      {
        question: "How long does it take to see results?",
        answer:
          "Most clients start noticing measurable improvements in 3–6 months depending on the competition and marketing strategies used.",
      },
      {
        question: "How are you using AI in digital marketing services?",
        answer:
          "We leverage AI tools for audience targeting, content personalization, ad optimization, and advanced analytics.",
      },
      {
        question: "How much do you charge for digital marketing services?",
        answer:
          "Our pricing depends on the scope of services. We offer flexible packages suitable for businesses of all sizes.",
      },
      {
        question: "Why choose Kreative Machinez?",
        answer:
          "We are an award-winning agency with years of expertise, a data-driven approach, and proven success stories.",
      },
    ],
    []
  );

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
    };

    emailjs
      .send(
        "service_8zzykj8",
        "template_cw87jni",
        templateParams,
        "I8gKRp0YvJkXLUqEc"
      )
      .then(
        (result) => {
          console.log(result.text);

          // ✅ Show SweetAlert success
          Swal.fire({
            title: "Message Sent!",
            text: "✅ Your message has been successfully sent.",
            icon: "success",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });

          // Clear form
          setFormData({ name: "", email: "", phone: "", message: "" });
        },
        (error) => {
          console.error(error.text);

          Swal.fire({
            title: "Oops!",
            text: "❌ Failed to send message. Please try again.",
            icon: "error",
            confirmButtonColor: "#d33",
            confirmButtonText: "OK",
          });
        }
      );
  };

  return (
    <>
      <Helmet>
        <title>
          Reach Bangalore Distance Education | Enroll & Learn Online
        </title>
        <meta
          name="description"
          content="Contact Bangalore Distance Education for guidance on online degree courses, distance learning courses in Bangalore, and flexible study options."
        />
      </Helmet>
      <div style={{ backgroundColor: "#F5F5F5" }}>
        {/* Hero Section */}
        <section className="contact-hero">
          <div className="overlay">
            <h1 className="contact-title">CONTACT US</h1>
          </div>
        </section>

        {/* Contact Info + Form + Map */}
        <section className="contact-wrapper">
          <div className="contact-box">
            {/* Left: Form */}
            <div className="contact-left">
              <p className="subtitle">Send Us a Message</p>
              <h2 className="title">How Can We Help You?</h2>

              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Your Name *"
                    required
                    name="name"
                    autoComplete="off"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
                <input
                  type="email"
                  placeholder="Your Email *"
                  required
                  autoComplete="off"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
                <input
                  type="number"
                  placeholder="Your Phone *"
                  required
                  autoComplete="off"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />

                <textarea
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                ></textarea>
                <br />
                <button type="submit" className="submit-btn">
                  Submit
                </button>
              </form>
            </div>

            {/* Right: Info + Map */}
            <div className="contact-right">
              <p className="orange">VISIT TEAM GEEK</p>
              <p className="info-text">
                Call us to schedule a tour of our Houston office, right inside
                the 610 Loop in the Galleria/Uptown area. We are open Monday
                through Friday from 8:30 am to 5:30 pm CST.
              </p>

              <ul className="info-list">
                <li>
                  <span className="icon">
                    <FaPhone />
                  </span>
                  <div>
                    <strong>Call Us</strong>
                    <p>9035600400</p>
                  </div>
                </li>
                <li>
                  <span className="icon">
                    <FaMapMarkerAlt />
                  </span>
                  <div>
                    <strong>Address</strong>
                    <p>Headquarter Hennur Road, Bangalore</p>
                  </div>
                </li>
              </ul>

              {/* Map inside contact box */}
              <div className="map-container mt-3">
                <Suspense fallback={<p>Loading map...</p>}>
                  <Map />
                </Suspense>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
